import { VIDEO_EVENTS } from '@bedrock/media-engine';
import { EVENTS } from '@bedrock-rdk/media-engine';
import { LAYOUT_ENTITIES } from '@bedrock/fido-sdk';

export const PLAYBACK_STATE_IDLE = 'idle';

export const PLAYBACK_STATE_PLAYING = 'playing';

export const PLAYBACK_STATE_PAUSED = 'paused';

export const PLAYBACK_STATE_ENDED = 'ended';

export const PLAYER_MODE = {
  // Should be displayed before video start and after mid-roll (or after mid-roll ad jingle clip, if any)
  VIDEO_JINGLE: 'VIDEO_JINGLE',
  // Should be displayed after video jingle on video start only
  PROGRAM_JINGLE: 'PROGRAM_JINGLE',
  // Just a regular video playback
  CONTENT: 'CONTENT',
  // Displayed before each ad break
  BEFORE_AD_JINGLE_IMAGE: 'BEFORE_AD_JINGLE_IMAGE',
  // Displayed before ad break, if configured, has priority over the ad jingle image if both are enabled
  BEFORE_AD_JINGLE_CLIP: 'BEFORE_AD_JINGLE_CLIP',
  // Just a regular advertisement
  AD: 'AD',
  // Displayed right after an ad break
  AFTER_AD_JINGLE_CLIP: 'AFTER_AD_JINGLE_CLIP',
  // Displayed after ad jingle clip or after ad break, if no clip present
  AFTER_AD_JINGLE_IMAGE: 'AFTER_AD_JINGLE_IMAGE',
};

// An ad jingle clip can play before the first ad of a break or after the last one.
export const AD_JINGLE_POSITION = {
  BEFORE: 'before',
  AFTER: 'after',
};

export const PAUSE_AD_STATE = {
  IDLE: 0,
  LOADING: 1,
  DISPLAYING: 2,
  ERROR: 3,
};

export const LBANNER_STATE = {
  HIDE: 0,
  SHOW: 1,
};

export const PLAYER_ENTITIES = [
  LAYOUT_ENTITIES.audio,
  LAYOUT_ENTITIES.radio,
  LAYOUT_ENTITIES.video,
  LAYOUT_ENTITIES.live,
];

export const JINGLE_DURATION = 3000;

// How long the video jingle is shown before switching to the program jingle.
export const PROGRAM_JINGLE_DELAY = 2000;

export const AD_JINGLE_DURATION = 1500;

export const INTERFACE_DISPLAY_DURATION = 5000;

export const SEEK_DEBOUNCE_MS = 800;

export const SEEK_THROTTLE_MS = 220;

export const SEEK_STEP_SECONDS = 15;

export const LIVE_DURATION = -1;

export const MIN_SECONDS_TO_TRACK_PROGRESS = 30;

export const AD_BREAK_MID_ROLL = 'midroll';

// Step of the content completion analytics events (25%, 50%, ...)
export const PERCENTAGE_EVENT_DURATION = 25;

export const PLAYER_ACTIONS_PREFIX = 'player';

export const withPlayerPrefix = event => `${PLAYER_ACTIONS_PREFIX}/${event.toUpperCase()}`;

export const INITIALIZED = withPlayerPrefix('INITIALIZED');

export const LOAD_START = withPlayerPrefix('LOAD_START');

export const MANIFEST_LOADED = withPlayerPrefix('MANIFEST_LOADED');

export const BUFFERING = withPlayerPrefix(VIDEO_EVENTS.LOAD_START);

export const LOCKED = withPlayerPrefix('LOCKED');

export const CAN_PLAY = withPlayerPrefix(VIDEO_EVENTS.CAN_PLAY);

export const CAN_PLAY_THROUGH = withPlayerPrefix(VIDEO_EVENTS.CAN_PLAY_THROUGH);

export const PLAYING = withPlayerPrefix(VIDEO_EVENTS.PLAYING);

export const PAUSED = withPlayerPrefix(VIDEO_EVENTS.PAUSE);

export const WAITING = withPlayerPrefix(VIDEO_EVENTS.WAITING);

export const TIME_UPDATE = withPlayerPrefix(VIDEO_EVENTS.TIME_UPDATE);

export const SEEKING = withPlayerPrefix(VIDEO_EVENTS.SEEKING);

export const SEEKED = withPlayerPrefix(VIDEO_EVENTS.SEEKED);

export const ENDED = withPlayerPrefix(VIDEO_EVENTS.ENDED);

export const QUALITY_CHANGED = withPlayerPrefix(VIDEO_EVENTS.QUALITY_CHANGE);

export const PLAYER_INFO = withPlayerPrefix(EVENTS.PLAYER_INFO);

export const TRACKS_CHANGED = withPlayerPrefix(EVENTS.TRACKS_CHANGED);

export const AUDIO_TRACK_CHANGED = withPlayerPrefix(EVENTS.AUDIO_TRACK_CHANGED);

export const SUBTITLES_TRACK_CHANGED = withPlayerPrefix(EVENTS.SUBTITLES_TRACK_CHANGED);

export const SUBTITLES_UPDATED = withPlayerPrefix(EVENTS.SUBTITLES_UPDATED);

export const TOP_BAR_BACK = withPlayerPrefix('TOP_BAR_BACK');

export const TOP_BAR_START_OVER = withPlayerPrefix('TOP_BAR_START_OVER');

export const TOP_BAR_TRACKS = withPlayerPrefix('TOP_BAR_TRACKS');

export const TOP_BAR_RELATED_VIDEOS = withPlayerPrefix('TOP_BAR_RELATED_VIDEOS');

export const TOP_BAR_BACK_TO_LIVE = withPlayerPrefix('TOP_BAR_BACK_TO_LIVE');

export const TOP_BAR_INFO_PAGE = withPlayerPrefix('TOP_BAR_INFO_PAGE');

export const RESET = withPlayerPrefix('RESET');

export const RESET_LAST_VIEWED_VIDEO_DATA = withPlayerPrefix('RESET_LAST_VIEWED_VIDEO_DATA');

export const AD_PROVIDER_LOADED = withPlayerPrefix('AD_PROVIDER_LOADED');

export const AD_MARKERS_LIST = withPlayerPrefix('AD_MARKERS_LIST');

export const AD_JINGLE_STARTED = withPlayerPrefix('AD_JINGLE_STARTED');

export const AD_JINGLE_COMPLETE = withPlayerPrefix('AD_JINGLE_COMPLETE');

// App-owned jingle surface transitions (video jingle -> program jingle; post-ad jingle).
export const SHOW_PROGRAM_JINGLE = withPlayerPrefix('SHOW_PROGRAM_JINGLE');

export const AD_BREAK_STARTED = withPlayerPrefix('AD_BREAK_STARTED');

export const AD_BREAK_ENDED = withPlayerPrefix('AD_BREAK_ENDED');

export const AD_STARTED = withPlayerPrefix('AD_STARTED');

export const AD_PLAYING = withPlayerPrefix('AD_PLAYING');

export const AD_JINGLE_CLIP_PLAYING = withPlayerPrefix('AD_JINGLE_CLIP_PLAYING');

export const AD_TIME_UPDATE = withPlayerPrefix('AD_TIME_UPDATE');

export const AD_PAUSED = withPlayerPrefix('AD_PAUSED');

export const AD_ENDED = withPlayerPrefix('AD_ENDED');

export const AD_ERROR = withPlayerPrefix('AD_ERROR');

export const SET_PAUSE_AD = withPlayerPrefix('SET_PAUSE_AD');

export const SET_PAUSE_AD_STATE = withPlayerPrefix('SET_PAUSE_AD_STATE');

export const APPEND_NEW_METADATA_ITEMS = withPlayerPrefix('APPEND_NEW_METADATA_ITEMS');

// NOTE: the panel content is paginated within the player state, as any modification of the layout
// cache restarts the playback (see the layout controllers of the player screens)
export const SIDE_GLASS_PAGE_REQUESTED = withPlayerPrefix('SIDE_GLASS_PAGE_REQUESTED');

export const SIDE_GLASS_PAGE_LOADED = withPlayerPrefix('SIDE_GLASS_PAGE_LOADED');

export const SIDE_GLASS_PAGE_FAILED = withPlayerPrefix('SIDE_GLASS_PAGE_FAILED');

export const UPDATE_METADATA = withPlayerPrefix(VIDEO_EVENTS.LOADED_METADATA);

export const SET_AUDIO_TRACK = withPlayerPrefix('SET_AUDIO_TRACK');

export const SET_SUBTITLE_TRACK = withPlayerPrefix('SET_SUBTITLE_TRACK');

export const TRACK_PREFERENCES_CHANGED = withPlayerPrefix('TRACK_PREFERENCES_CHANGED');

export const SET_CURRENT_METADATA_ITEM = withPlayerPrefix('SET_CURRENT_METADATA_ITEM');

export const SET_LBANNER_STATE = withPlayerPrefix('SET_LBANNER_STATE');

export const SET_PLAYER_ERROR = withPlayerPrefix('SET_PLAYER_ERROR');

export const SWITCH_TO_NEXT_SOURCE = withPlayerPrefix('SWITCH_TO_NEXT_SOURCE');

/**
 * Creates item-dependent action that dispatches appropriate Redux actions
 *
 * @param {Object} options all the options
 * @param {Object} options.item item metadata, as retrieved from layout
 * @param {Object} options.isLive is content is Live
 * @param {string} options.layout layout object, required for Lock actions
 * @param {string} options.entityId layout entity ID, required for player initialization
 */
export const setPlayerInitialized = options => ({
  ...options,
  type: INITIALIZED,
});

export const loadStart = () => ({ type: LOAD_START });

export const lockPlayer = () => ({ type: LOCKED });

export const playing = () => ({ type: PLAYING });

export const paused = () => ({ type: PAUSED });

export const reset = () => ({ type: RESET });

export const resetLastViewedData = () => ({ type: RESET_LAST_VIEWED_VIDEO_DATA });

export const adProviderLoaded = () => ({ type: AD_PROVIDER_LOADED });

export const adMarkers = markers => ({ type: AD_MARKERS_LIST, markers });

export const adJingleStarted = () => ({ type: AD_JINGLE_STARTED });

export const adJingleComplete = () => ({ type: AD_JINGLE_COMPLETE });

export const showProgramJingle = () => ({ type: SHOW_PROGRAM_JINGLE });

export const adBreakStarted = (type, totalDuration) => ({ type: AD_BREAK_STARTED, adType: type, totalDuration });

export const adBreakEnded = () => ({ type: AD_BREAK_ENDED });

export const adStarted = skipOffset => ({ type: AD_STARTED, skipOffset });

export const adPlaying = () => ({ type: AD_PLAYING });

export const adJingleClipPlaying = position => ({ type: AD_JINGLE_CLIP_PLAYING, position });

/**
 * @param {number} timeInSeconds elapsed time of the current ad, drives its skip button
 * @param {number} breakTimeInSeconds elapsed time of the whole break, drives its countdown
 */
export const adTimeUpdate = (timeInSeconds, breakTimeInSeconds) => ({
  type: AD_TIME_UPDATE,
  currentTime: timeInSeconds,
  breakTime: breakTimeInSeconds,
});

export const adPaused = () => ({ type: AD_PAUSED });

export const adEnded = () => ({ type: AD_ENDED });

export const adError = ({ adRequestUrl, content, context, details, fatal, monitored, reason }) => ({
  type: AD_ERROR,
  adRequestUrl,
  content,
  context,
  details,
  fatal,
  monitored,
  reason,
});

export const setPauseAd = ad => ({ type: SET_PAUSE_AD, ad });

export const pauseAdsError = () => ({ type: SET_PAUSE_AD_STATE, state: PAUSE_AD_STATE.ERROR });

export const pauseAdsDisplaying = () => ({ type: SET_PAUSE_AD_STATE, state: PAUSE_AD_STATE.DISPLAYING });

export const setLBannerShow = () => ({ type: SET_LBANNER_STATE, state: LBANNER_STATE.SHOW });

export const setLBannerHide = () => ({ type: SET_LBANNER_STATE, state: LBANNER_STATE.HIDE });

export const setPlayerError = error => ({ type: SET_PLAYER_ERROR, error });

// Switch playback to a fallback source after the current one failed to start.
export const switchToNextSource = sourceIndex => ({ type: SWITCH_TO_NEXT_SOURCE, sourceIndex });
